import {Injector, LOCALE_ID, NgModule} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {MainComponent} from "./main.component";
import {
    CommonModule,
    HashLocationStrategy,
    LocationStrategy,
    registerLocaleData
} from "@angular/common";
import {
    DefaultDataServiceConfig,
    EntityCollectionReducerMethodsFactory,
    EntityDataModule,
    PersistenceResultHandler
} from "@ngrx/data";
import {entityConfig} from "../../store/entity/entity-metadata";
import {environment} from "../../../environments/environment";
import {StoreDevtoolsModule} from "@ngrx/store-devtools";
import {PagePersistenceResultHandler} from "../../store/core/pagination/pagePersistenceResultHandler";
import {
    EntityCollectionPageReducerMethodsFactory
} from "../../store/core/pagination/pageEntityCollectionReducerMethods";
import {defaultDataServiceConfig} from "../../store/core/defaultDataServiceConfig";
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";
import {StoreModule} from "@ngrx/store";
import {AccountService} from "../../services/account/account.service";
import {AgendaEventModule} from "../../modules/agenda/modules/event/agenda-event.module";
import {CalendarModule} from "../../modules/agenda/modules/calendar/calendar.module";
import {RouterModule, Routes} from "@angular/router";
import {AppInjector} from "../../app-injector";
import {StringUtils} from "../../shared/utils/string.utils";
import {LoadingModule} from "../../components/common/loading/loading.module";
import {
    IconX,
    IconHome,
    IconRefresh,
    IconArrowBack,
    IconDeviceFloppy,
    IconRuler2,
    IconCircleCheck, IconCircleDashed, IconTrash
} from "angular-tabler-icons/icons";
import {InterceptorService} from "./modules/core/interceptor.service";
import {ErrorCardModule} from "../../components/common/error-card/error-card.module";
import {StickyContainerModule} from "../../modules/core/component/sticky-container/sticky-container.module";
import {ButtonComponent} from "./modules/core/components/api/button/button.component";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import localeFr from '@angular/common/locales/fr';
import {ConsultationModule} from "../../modules/medical/consultation/consultation.module";
import {ExamFormModule} from "../../modules/exam-form/exam-form.module";
import {MedicalReportModule} from "../../modules/medical/report/medicalReport.module";
import {
    CommunicationConfigurationModule
} from "../../modules/communication/configuration/communicationConfiguration.module";
import {MedicalTreatmentModule} from "../../modules/medical/treatment/medicalTreatment.module";
import {MedicalChargedActModule} from "../../modules/medical/charged-act/medicalChargedAct.module";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {LoggedUserGuard} from "../../modules/auth/api/guard/loggedUser.guard";
import {IconsModule} from "../../../icons.module";
import {ExamenDefinitionModule} from "../../modules/examen/definition/examenDefinition.module";
import {InsolesModule} from "../../modules/insole/insoles/insoles.module";
import {ProviderModule} from "../../modules/provider/provider.module";
import {InsoleModule} from "../../modules/insole/insole.module";
import {
    AiRealtimeAudioComponent
} from "../../modules/ai/realtime/api/component/ai-realtime-audio/ai-realtime-audio.component";
import {MedicalModule} from "../../modules/medical/medical.module";
import {provideAiAppEventRenderer} from "../../modules/ai/realtime/impl/service/aiAppEventRenderer.service";
import {
    ConsultationSportAiEventComponent
} from "./modules/consultation/api/components/consultation-sport-ai-event/consultation-sport-ai-event.component";
import {AiAppEventRendererRegistry} from "../../modules/ai/realtime/impl/service/aiAppEventRenderer.registry";
import {
    ConsultationVisitReasonAiEventComponent
} from "./modules/consultation/api/components/consultation-visit-reason-ai-event/consultation-visit-reason-ai-event.component";
import {
    ConsultationPainAiEventComponent
} from "./modules/consultation/api/components/consultation-pain-ai-event/consultation-pain-ai-event.component";
import {
    ConsultationProblemAiEventComponent
} from "./modules/consultation/api/components/consultation-problem-ai-event/consultation-problem-ai-event.component";
import {
    ConsultationMorphologyAiEventComponent
} from "./modules/consultation/api/components/consultation-morphology-ai-event/consultation-morphology-ai-event.component";
import {
    ConsultationChargedActAiEventComponent
} from "./modules/consultation/api/components/consultation-charged-act-ai-event/consultation-charged-act-ai-event.component";

const icons = {
    IconX, IconHome, IconRefresh, IconArrowBack, IconRuler2, IconCircleCheck, IconCircleDashed, IconTrash, IconDeviceFloppy}

const routes: Routes = [
    {
        path: 'luna-app/home',
        loadComponent: () => import('./modules/home/api/components/home/home.component')
            .then(m => m.HomeComponent),
        canActivate: [LoggedUserGuard]
    },
    {
        path: 'luna-app/consultations/:id',
        loadChildren: () =>
            import('./modules/consultation/api/components/consultation/consultation.route')
                .then(m => m.CONSULTATION_ROUTES),
        canActivate: [LoggedUserGuard]

    },
    {
        path: 'luna-app/login-from-token',
        loadComponent: () =>
            import('./modules/core/login/impl/components/login-from-token/login-from-token.component')
                .then(m => m.LoginFromTokenComponent),

    },
    {
        path: 'luna-app/account-change/:accountBusinessCode',
        loadChildren: () => import('../../modules/account/common/impl/page/account-change/account-change.module')
            .then(m => m.AccountChangePageModule),
    },
    {
        path: 'luna-app/login-from-device',
        loadComponent: () =>
            import('./modules/core/login/impl/components/login-from-device/login-from-device.component')
                .then(m => m.LoginFromDeviceComponent),

    },
    {
        path: 'authentication/login',
        loadComponent: () =>
            import('./modules/core/login/impl/components/login/login.component')
                .then(m => m.LoginComponent),

    },
    { path: '', pathMatch: 'full', redirectTo: 'luna-app/home'}
];

registerLocaleData(localeFr, 'fr');

@NgModule({
    imports: [BrowserModule,
        BrowserAnimationsModule,
        CommonModule,
        EntityDataModule.forRoot(entityConfig),
        !environment.production ? StoreDevtoolsModule.instrument() : [],
        HttpClientModule,
        StoreModule.forRoot(),
        RouterModule.forRoot([]),
        MatSnackBarModule,

        IconsModule,
        AgendaEventModule,
        ConsultationModule,
        ExamFormModule,
        MedicalModule,
        CommunicationConfigurationModule,
        CalendarModule, LoadingModule, ErrorCardModule,
        ExamenDefinitionModule,
        ProviderModule,
        InsoleModule,
        RouterModule.forChild(routes), StickyContainerModule, ButtonComponent, AiRealtimeAudioComponent,

    ],

    declarations: [MainComponent],
    bootstrap: [MainComponent],
    providers: [
        AccountService,
        {provide: LOCALE_ID, useValue: 'fr-FR'},
        {provide: MAT_DIALOG_DATA, useValue: {}},
        {provide: MatDialogRef, useValue: {}},
        {provide: PersistenceResultHandler, useClass: PagePersistenceResultHandler},
        {provide: EntityCollectionReducerMethodsFactory, useClass: EntityCollectionPageReducerMethodsFactory},
        {provide: DefaultDataServiceConfig, useValue: defaultDataServiceConfig},
        {provide: LocationStrategy, useClass: HashLocationStrategy},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InterceptorService,
            multi: true
        },
        // AI
        AiAppEventRendererRegistry,
        provideAiAppEventRenderer(ConsultationSportAiEventComponent),
        provideAiAppEventRenderer(ConsultationVisitReasonAiEventComponent),
        provideAiAppEventRenderer(ConsultationPainAiEventComponent),
        provideAiAppEventRenderer(ConsultationProblemAiEventComponent),
        provideAiAppEventRenderer(ConsultationMorphologyAiEventComponent),
        provideAiAppEventRenderer(ConsultationChargedActAiEventComponent)
    ],
})
export class MainModule {
    constructor(private injector: Injector) {
        AppInjector.injector = injector;
        this.overrideDate();
    }

    overrideDate() {
        // Override isoString date to fix error on hours due to wrong GMT
        Date.prototype.toISOString = function () {
            return this.getFullYear() +
                '-' + pad(this.getMonth() + 1) +
                '-' + pad(this.getDate()) +
                'T' + pad(this.getHours()) +
                ':' + pad(this.getMinutes()) +
                ':' + pad(this.getSeconds());
        };


        function pad(nb: number): string {
            return StringUtils.leftPad(String(nb), 2, '0');
        }
    }
}